import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import { FC, ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'
import { Scroller } from '../Scroller/Scroller'
import { ContentWidth, TextWidth } from 'components/Container'
import { AnnotationElement } from 'components/AnnotationProject/AnnotationElement'
import { getAnnotations_Huhn } from './annotation_chicken'
import { getAnnotations_Schwein } from './annotation_pig'
import { getAnnotations_Kuh } from './annotation_cow'
import { useWindowSize } from 'usehooks-ts'
import { Annotation } from './Annotation'

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
}): ReactElement => {
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]
  const [width, setWidth] = useState<number | undefined>()
  const windowSize = useWindowSize()
  useEffect(() => {
    setWidth(0)
  }, [])
  useEffect(() => {
    setWidth(windowSize.width)
  }, [windowSize.width])
  const CM = colorMode === 'light' ? 'LM' : 'DM'

  return (
    <AppContainer className={globalClasses.join(' ')} $colorMode={colorMode}>
      <Scroller
        defaultDistance={400}
        front={[
          <FrontElement distanceTop={1} distanceBottom={1}>
            <TextWidth>
              <TextBackground special>
                <h3>Das Huhn</h3>
                <p>
                  In der Schweiz sind Hühner die am meisten wachsende
                  Nutztierart: In den letzten 20 Jahren hat sich ihr Bestand
                  beinahe verdoppelt &mdash; ein Plus von 91 Prozent auf heute
                  knapp <b>13 Millionen Hühner</b> per Stichtag. Da die meisten
                  Nutzhühner weniger als ein Jahr leben, liegt der Jahresbestand
                  weit darüber. Die grössten Gruppen bilden Mastpoulets und
                  Legehennen. Die Minimalanforderungen bei der Haltung von{' '}
                  <b>Legehennen</b> sehen wie folgt aus:
                </p>
              </TextBackground>
            </TextWidth>
          </FrontElement>,
          <FrontElement className='Bernd'>
            <Scroller
              front={[
                <FrontElement distanceBottom={1000}>
                  <TextWidth>
                    <TextBackground>
                      <p>
                        In Ställen, in denen Legehennen gehalten werden, gelten
                        je nach Zahl der Tiere andere gesetzliche Vorgaben.
                        Leben bis zu 150 Tiere in einem Stall, wird für 7 Hennen
                        ein Quadratmeter gerechnet. Sind es mehr Tiere, wird
                        zwischen{' '}
                        <b>Gitterfläche (12,5 Tiere pro Quadratmeter)</b> und{' '}
                        <b>Einstreufläche (3,5 Tiere pro Quadratmeter)</b>{' '}
                        unterschieden. Die mit einem geeigneten Material, wie
                        zum Beispiel Stroh,{' '}
                        <b>eingestreute Fläche muss mindestens 20 Prozent</b>{' '}
                        der begehbaren Fläche betragen und für die Hühner
                        während der natürlichen oder künstlichen Tageslichtphase
                        zugänglich sein.
                      </p>
                      <p>
                        In der Tierschutzverordnung ist auch festgehalten, dass
                        dem Geflügel «genügend Fütterungs- und
                        Tränkeeinrichtungen zur Verfügung stehen» müssen. Für
                        die <b>manuelle Fütterung</b> werden für{' '}
                        <b>jede Henne 16 Zentimeter Breite am Trog</b>{' '}
                        gerechnet. Wird das Futter mechanisch bereitgestellt,
                        sind es noch 8 Zentimeter.{' '}
                        <b>An einer Rundtränke sind es 1,5 Zentimeter</b>.
                      </p>
                      <p>
                        Für das Eierlegen ist festgelegt, dass{' '}
                        <b>sich maximal fünf Hühner ein Einzelnest teilen</b>.
                        Sind im Stall Gruppennester installiert, müssen diese so
                        gross sein, dass sich maximal{' '}
                        <b>100 Hennen einen Quadratmeter des Nestes</b> teilen.
                      </p>
                      <p>
                        Zum Schlafen kann sich jedes Huhn auf eine der
                        Sitzstangen zurückziehen – da sind pro Tier 14
                        Zentimeter vorgesehen.
                      </p>
                    </TextBackground>
                  </TextWidth>
                </FrontElement>,
              ]}
              back={(index) => {
                const AN_ELEM = (
                  <AnnotationElement
                    defaults={{
                      anchor: 'top',
                      distance: 50,
                      connect: false,
                    }}
                    annotations={getAnnotations_Huhn(
                      colorMode,
                      width,
                      'konventionell'
                    )}
                  >
                    <img
                      alt=''
                      src={
                        width > 831
                          ? require('../../assets/H_Large_' +
                              CM +
                              '_konventione.png')
                          : width > 542
                          ? require('../../assets/H_Medium_' +
                              CM +
                              '_konventione.png')
                          : require('../../assets/H_Small_' +
                              CM +
                              '_konventione.png')
                      }
                    />
                  </AnnotationElement>
                )
                return (
                  <BackElement>
                    <div style={{ position: 'relative' }}>
                      <TextWidth>
                        <StandardLabel>Gesetzliches Minimum</StandardLabel>
                      </TextWidth>
                      {width > 830 && <ContentWidth>{AN_ELEM}</ContentWidth>}
                      {width < 832 && <FullWidth>{AN_ELEM}</FullWidth>}
                    </div>
                  </BackElement>
                )
              }}
            />
          </FrontElement>,
          <FrontElement>
            <Scroller
              front={[
                <FrontElement distanceBottom={1000}>
                  <TextWidth>
                    <TextBackground>
                      <p>
                        In den Bio-Suisse Richtlinien von 2018 ist über
                        gesetzlichen Mindeststandards hinaus vorgesehen, dass{' '}
                        <b>maximal 2000 Legehennen in einem Stall</b> gehalten
                        werden dürfen. Von diesen Ställen darf es{' '}
                        <b>pro Betrieb maximal zwei</b> geben.
                      </p>
                      <p>
                        Die Tiere hier müssen sich <b>einen Quadratmeter</b> der
                        begehbaren Fläche <b>mit nur 5 anderen Hennen teilen</b>
                        . Ein{' '}
                        <b>
                          Drittel des Stalles muss als Scharrfläche eingerichtet
                        </b>{' '}
                        sein.
                      </p>
                      <p>
                        <b>Zwingend</b> für die Haltung nach den
                        Bio-Suisse-Richtlinien ist ein{' '}
                        <b>
                          überdachter Aussenbereich mit einer maximalen Dichte
                          von 10 Tieren pro Quadratmeter
                        </b>{' '}
                        sowie ein Auslauf ins Grüne oder auf eine Weide. Jedes
                        Tier hat das Recht auf 5 Quadratmeter Auslauf.
                      </p>
                    </TextBackground>
                  </TextWidth>
                </FrontElement>,
              ]}
              back={(index) => {
                const AN_ELEM = (
                  <AnnotationElement
                    defaults={{
                      anchor: 'top',
                      distance: 120,
                      connect: false,
                    }}
                    annotations={getAnnotations_Huhn(colorMode, width, 'bio')}
                  >
                    <img
                      alt=''
                      src={
                        width > 831
                          ? require('../../assets/H_Large_' + CM + '_bio.png')
                          : width > 542
                          ? require('../../assets/H_Medium_' + CM + '_bio.png')
                          : require('../../assets/H_Small_' + CM + '_bio.png')
                      }
                    />
                  </AnnotationElement>
                )
                return (
                  <BackElement>
                    <div style={{ position: 'relative' }}>
                      <TextWidth>
                        <StandardLabel>Bio-Suisse</StandardLabel>
                      </TextWidth>
                      {width > 830 && <ContentWidth>{AN_ELEM}</ContentWidth>}
                      {width < 832 && <FullWidth>{AN_ELEM}</FullWidth>}
                    </div>
                  </BackElement>
                )
              }}
            />
          </FrontElement>,
          <FrontElement>
            <Scroller
              front={[
                <FrontElement distanceBottom={1000}>
                  <TextWidth>
                    <TextBackground>
                      <p>
                        Die EU-Verordnung zum Schutz landwirtschaftlicher
                        Nutztiere legt fest, dass sich jede Legehenne{' '}
                        <b>einen Quadratmeter mit maximal 9 anderen Tieren</b>{' '}
                        teilen muss. Der{' '}
                        <b>
                          vorgeschriebene Einstreubereich muss ein Drittel der
                          Stallfläche
                        </b>
                        , aber <b>mindestens 250 Quadratzentimeter</b> pro Huhn
                        umfassen.
                      </p>
                      <p>
                        Ein <b>Einzelnest müssen sich 7 Hennen</b> teilen, in
                        einem{' '}
                        <b>Gruppennest sind es 120 Tiere pro Quadratmeter</b>.
                      </p>
                    </TextBackground>
                  </TextWidth>
                </FrontElement>,
              ]}
              back={(index) => {
                const AN_ELEM = (
                  <AnnotationElement
                    defaults={{
                      anchor: 'top',
                      distance: 120,
                      connect: false,
                    }}
                    annotations={getAnnotations_Huhn(colorMode, width, 'eu')}
                  >
                    <img
                      alt=''
                      src={
                        width > 831
                          ? require('../../assets/H_Large_' + CM + '_eu.png')
                          : width > 542
                          ? require('../../assets/H_Medium_' + CM + '_eu.png')
                          : require('../../assets/H_Small_' + CM + '_eu.png')
                      }
                    />
                  </AnnotationElement>
                )
                return (
                  <BackElement>
                    <div style={{ position: 'relative' }}>
                      <TextWidth>
                        <StandardLabel>EU-Richtlinien</StandardLabel>
                      </TextWidth>
                      {width > 830 && <ContentWidth>{AN_ELEM}</ContentWidth>}
                      {width < 832 && <FullWidth>{AN_ELEM}</FullWidth>}
                    </div>
                  </BackElement>
                )
              }}
            />
          </FrontElement>,
          <FrontElement distanceBottom={200}>
            <SizeCompare>
              <TextWidth>
                <TextBackground>
                  <h4>Vergleich der vorgeschriebenen minimalen Fläche</h4>
                  <AnnotationElement
                    defaults={{
                      distance: 0,
                    }}
                    annotations={[
                      {
                        left: 5,
                        top: 25,
                        anchor: 'top',
                        content: (
                          <Annotation
                            colorMode={colorMode}
                            style={{ minWidth: 100 }}
                          >
                            <span style={{ color: '#D3343F' }}>
                              Gesetzliches Minimum
                            </span>
                            <br />
                            0,08m<sup>2</sup>
                            <small>auf Gitterflächen</small>
                          </Annotation>
                        ),
                        // connect: true,
                        connector: {
                          type: 'straight',
                          style: { stroke: '#D3343F', strokeWidth: 1.5 },
                          offset: 0,
                        },
                      },
                      {
                        left: 70,
                        top: 25,
                        anchor: 'top',
                        content: (
                          <Annotation
                            align='left'
                            colorMode={colorMode}
                            style={{ minWidth: 80 }}
                          >
                            <span style={{ color: '#2CA29F' }}>Bio-Suisse</span>
                            <br />
                            5,2m<sup>2</sup>
                          </Annotation>
                        ),
                        // connect: true,
                        connector: {
                          type: 'straight',
                          style: { stroke: '#2CA29F', strokeWidth: 1.5 },
                          offset: 0,
                        },
                      },
                      {
                        left: 5,
                        top: 70,
                        anchor: 'bottom',
                        content: (
                          <Annotation
                            colorMode={colorMode}
                            style={{ minWidth: 80 }}
                          >
                            <span
                              style={{
                                color: CM === 'DM' ? '#e9e9e9' : '#202346',
                              }}
                            >
                              EU-Richtlinien
                            </span>
                            <br />
                            0,11m<sup>2</sup>
                          </Annotation>
                        ),
                        // connect: true,
                        connector: {
                          type: 'straight',
                          style: {
                            stroke: CM === 'DM' ? '#e9e9e9' : '#202346',
                            strokeWidth: 1.5,
                          },
                          offset: 0,
                        },
                      },
                    ]}
                  >
                    <img
                      alt=''
                      src={
                        CM === 'DM'
                          ? require('../../assets/H_DM.png')
                          : require('../../assets/H.png')
                      }
                    />
                  </AnnotationElement>
                </TextBackground>
              </TextWidth>
            </SizeCompare>
          </FrontElement>,
        ]}
        back={(index) => {
          console.log(index)

          let bgimage =
            width > 831
              ? require('../../assets/H_Large_' + CM + '_huhn_2.png')
              : width > 542
              ? require('../../assets/H_Medium_' + CM + '_huhn_2.png')
              : require('../../assets/H_Small_' + CM + '_huhn_2.png')

          if (index >= 1)
            bgimage =
              width > 831
                ? require('../../assets/H_Large_' + CM + '_huhn.png')
                : width > 542
                ? require('../../assets/H_Medium_' + CM + '_huhn.png')
                : require('../../assets/H_Small_' + CM + '_huhn.png')

          let El = <img alt='' src={bgimage} />
          let Label = 'Beispiel: Legehenne'
          return (
            <BackElement>
              {index >= 1 && (
                <TextWidth>
                  <AnimalLabel>{Label}</AnimalLabel>
                </TextWidth>
              )}
              {width > 830 && <ContentWidth>{El}</ContentWidth>}
              {width < 832 && <FullWidth>{El}</FullWidth>}
            </BackElement>
          )
        }}
      />
      <Scroller
        front={[
          <FrontElement distanceTop={1} distanceBottom={1}>
            <TextWidth>
              <TextBackground special>
                <h3>Das Schwein</h3>
                <p>
                  Seit Mitte der Nullerjahre ist die Anzahl der Schweine
                  rückläufig. Noch immer leben aber{' '}
                  <b>1,3 Millionen Schweine</b> in der Schweiz. 37 Prozent davon
                  leben in 339 Grossbetrieben (mehr als 500 Schweine pro
                  Betrieb) &mdash; davon erfüllen zwei die Bio-Suisse-Standards.
                </p>
              </TextBackground>
            </TextWidth>
          </FrontElement>,
          <FrontElement>
            <Scroller
              front={[
                <FrontElement distanceBottom={1000}>
                  <TextWidth>
                    <TextBackground>
                      <p>
                        <b>0,9 Quadratmeter</b> gesteht die Tierschutzverordnung{' '}
                        <b>jedem Mastschwein</b> mit einem Gewicht von 85 bis
                        110 Kilogramm zu. Davon gelten{' '}
                        <b>0,6 Quadratmeter als Liegefläche</b>, welche sich vom
                        Rest durch abweichende Vorschriften für die Dichte der
                        Perforation im Boden unterscheiden.
                      </p>
                      <p>
                        Zur Beschäftigung der Schweine muss im Stall jederzeit
                        Stroh, Raufutter (z.B. Getreidespreu) oder anderes
                        gleichwertiges Material vorhanden sein.
                      </p>
                    </TextBackground>
                  </TextWidth>
                </FrontElement>,
              ]}
              back={(index) => {
                const AN_ELEM = (
                  <AnnotationElement
                    defaults={{
                      anchor: 'top',
                      distance: 50,
                      connect: false,
                    }}
                    annotations={getAnnotations_Schwein(
                      colorMode,
                      width,
                      'konventionell'
                    )}
                  >
                    <img
                      alt=''
                      src={
                        width > 831
                          ? require('../../assets/S_Large_' +
                              CM +
                              '_konventione.png')
                          : width > 542
                          ? require('../../assets/S_Medium_' +
                              CM +
                              '_konventione.png')
                          : require('../../assets/S_Small_' +
                              CM +
                              '_konventione.png')
                      }
                    />
                  </AnnotationElement>
                )
                return (
                  <BackElement>
                    <div style={{ position: 'relative' }}>
                      <TextWidth>
                        <StandardLabel>Gesetzliches Minimum</StandardLabel>
                      </TextWidth>
                      {width > 830 && <ContentWidth>{AN_ELEM}</ContentWidth>}
                      {width < 832 && <FullWidth>{AN_ELEM}</FullWidth>}
                    </div>
                  </BackElement>
                )
              }}
            />
          </FrontElement>,
          <FrontElement>
            <Scroller
              front={[
                <FrontElement distanceBottom={1000}>
                  <TextWidth>
                    <TextBackground>
                      <p>
                        Schweine, die nach den Bio-Suisse-Richtlinien gehalten
                        werden, haben etwas mehr Komfort.{' '}
                        <b>1,65 Quadratmeter Platz</b> muss jedes Tier
                        mindestens haben. <b>0,6</b> davon sind wie auch in der
                        Tierschutzverordnung als <b>Liegefläche</b> definiert.
                        Diese darf allerdings nicht mit Spaltenböden ausgelegt
                        sein und muss <b>mit Stroh in Bio-Suisse-Qualität</b>{' '}
                        eingestreut sein.
                      </p>
                      <p>
                        <b>
                          Jedes Tier hat Anspruch auf einen täglichen Auslauf
                        </b>{' '}
                        &mdash; dafür sind pro Schwein mindestens 0,65
                        Quadratmeter vorgesehen. Insgesamt muss die
                        Auslauffläche mindestens 10 Quadratmeter betragen.
                      </p>
                      <p>
                        Auch beim Bio-Suisse-Standard muss den Schweinen im
                        Stall «Beschäftigungsmaterial» zur Verfügung gestellt
                        werden. Hierzu zählt auch die Einstreu aus dem
                        Liegebereich.
                      </p>
                    </TextBackground>
                  </TextWidth>
                </FrontElement>,
              ]}
              back={(index) => {
                const AN_ELEM = (
                  <AnnotationElement
                    defaults={{
                      anchor: 'top',
                      distance: 120,
                      connect: false,
                    }}
                    annotations={getAnnotations_Schwein(
                      colorMode,
                      width,
                      'bio'
                    )}
                  >
                    <img
                      alt=''
                      src={
                        width > 831
                          ? require('../../assets/S_Large_' + CM + '_bio.png')
                          : width > 542
                          ? require('../../assets/S_Medium_' + CM + '_bio.png')
                          : require('../../assets/S_Small_' + CM + '_bio.png')
                      }
                    />
                  </AnnotationElement>
                )
                return (
                  <BackElement>
                    <div style={{ position: 'relative' }}>
                      <TextWidth>
                        <StandardLabel>Bio-Suisse</StandardLabel>
                      </TextWidth>
                      {width > 830 && <ContentWidth>{AN_ELEM}</ContentWidth>}
                      {width < 832 && <FullWidth>{AN_ELEM}</FullWidth>}
                    </div>
                  </BackElement>
                )
              }}
            />
          </FrontElement>,
          <FrontElement distanceBottom={0}>
            <Scroller
              front={[
                <FrontElement distanceBottom={1000}>
                  <TextWidth>
                    <TextBackground>
                      <p>
                        In den Ländern der EU steht jedem{' '}
                        <b>
                          Schwein mit einem Gewicht von 85 bis 110 Kilogramm ein
                          Platz von 0,75 Quadratmeter
                        </b>{' '}
                        zu. Die EU-Richtlinien über die Mindestanforderungen für
                        den Schutz von Schweinen sehen darüber hinaus vor, dass
                        Schweine einen «Zugang zu einem grössen- und
                        temperaturmässig angemessenen Liegebereich» haben
                        müssen, in dem «alle Tiere gleichzeitig liegen können».
                        Wie dieser ausgestaltet sein soll, ist nicht definiert.
                        Material, mit dem sich die Schweine beschäftigen können,
                        ist auch in der EU vorgeschrieben.
                      </p>
                    </TextBackground>
                  </TextWidth>
                </FrontElement>,
              ]}
              back={(index) => {
                const AN_ELEM = (
                  <AnnotationElement
                    defaults={{
                      anchor: 'top',
                      distance: 120,
                      connect: false,
                    }}
                    annotations={getAnnotations_Schwein(colorMode, width, 'eu')}
                  >
                    <img
                      alt=''
                      src={
                        width > 831
                          ? require('../../assets/S_Large_' + CM + '_eu.png')
                          : width > 542
                          ? require('../../assets/S_Medium_' + CM + '_eu.png')
                          : require('../../assets/S_Small_' + CM + '_eu.png')
                      }
                    />
                  </AnnotationElement>
                )
                return (
                  <BackElement>
                    <div style={{ position: 'relative' }}>
                      <TextWidth>
                        <StandardLabel>EU-Richtlinien</StandardLabel>
                      </TextWidth>
                      {width > 830 && <ContentWidth>{AN_ELEM}</ContentWidth>}
                      {width < 832 && <FullWidth>{AN_ELEM}</FullWidth>}
                    </div>
                  </BackElement>
                )
              }}
            />
          </FrontElement>,
          <FrontElement distanceBottom={200}>
            <SizeCompare>
              <TextWidth>
                <TextBackground>
                  <h4>Vergleich der vorgeschriebenen minimalen Fläche</h4>
                  <AnnotationElement
                    defaults={{
                      distance: 0,
                    }}
                    annotations={[
                      {
                        left: 5,
                        top: 25,
                        anchor: 'top',
                        content: (
                          <Annotation
                            colorMode={colorMode}
                            style={{ minWidth: 100 }}
                          >
                            <span style={{ color: '#D3343F' }}>
                              Gesetzliches Minimum
                            </span>
                            <br />
                            0,9m<sup>2</sup>
                          </Annotation>
                        ),
                        // connect: true,
                        connector: {
                          type: 'straight',
                          style: { stroke: '#D3343F', strokeWidth: 1.5 },
                          offset: 0,
                        },
                      },
                      {
                        left: 70,
                        top: 25,
                        anchor: 'top',
                        content: (
                          <Annotation
                            align='left'
                            colorMode={colorMode}
                            style={{ minWidth: 80 }}
                          >
                            <span style={{ color: '#2CA29F' }}>Bio-Suisse</span>
                            <br />
                            1,65m<sup>2</sup>
                          </Annotation>
                        ),
                        // connect: true,
                        connector: {
                          type: 'straight',
                          style: { stroke: '#2CA29F', strokeWidth: 1.5 },
                          offset: 0,
                        },
                      },
                      {
                        left: 5,
                        top: 70,
                        anchor: 'bottom',
                        content: (
                          <Annotation
                            colorMode={colorMode}
                            style={{ minWidth: 80 }}
                          >
                            <span
                              style={{
                                color: CM === 'DM' ? '#e9e9e9' : '#202346',
                              }}
                            >
                              EU-Richtlinien
                            </span>
                            <br />
                            0,75m<sup>2</sup>
                          </Annotation>
                        ),
                        // connect: true,
                        connector: {
                          type: 'straight',
                          style: {
                            stroke: CM === 'DM' ? '#e9e9e9' : '#202346',
                            strokeWidth: 1.5,
                          },
                          offset: 0,
                        },
                      },
                    ]}
                  >
                    <img
                      alt=''
                      src={
                        CM === 'DM'
                          ? require('../../assets/S_DM.png')
                          : require('../../assets/S.png')
                      }
                    />
                  </AnnotationElement>
                </TextBackground>
              </TextWidth>
            </SizeCompare>
          </FrontElement>,
        ]}
        back={(index) => {
          console.log(index)

          let bgimage =
            width > 831
              ? require('../../assets/S_Large_' + CM + '_schwein_2.png')
              : width > 542
              ? require('../../assets/S_Medium_' + CM + '_schwein_2.png')
              : require('../../assets/S_Small_' + CM + '_schwein_2.png')
          if (index >= 1)
            bgimage =
              width > 831
                ? require('../../assets/S_Large_' + CM + '_schwein.png')
                : width > 542
                ? require('../../assets/S_Medium_' + CM + '_schwein.png')
                : require('../../assets/S_Small_' + CM + '_schwein.png')

          let El = <img alt='' src={bgimage} />
          let Label = 'Beispiel: 100kg Mastschwein '
          return (
            <BackElement>
              {index >= 1 && (
                <TextWidth>
                  <AnimalLabel>{Label}</AnimalLabel>
                </TextWidth>
              )}
              {width > 830 && <ContentWidth>{El}</ContentWidth>}
              {width < 832 && <FullWidth>{El}</FullWidth>}
            </BackElement>
          )
        }}
      />
      <Scroller
        front={[
          <FrontElement distanceTop={1} distanceBottom={1}>
            <TextWidth>
              <TextBackground special>
                <h3>Das Rind</h3>

                <p>
                  Knapp 1,5 Millionen Rinder leben in der Schweiz. Ein Drittel
                  davon, die grösste Gruppe, sind <b>Milchkühe</b>. Bio-Suisse
                  erweitert die gesetzlichen Mindestbestimmungen um die
                  Teilnahme am <b>Raus-Programm</b>, was «Regelmässiger Auslauf
                  der Tiere» bedeutet. Bereits 88 Prozent der Milchkühe
                  profitieren von diesem Programm &mdash; weil der Bund die
                  Teilnahme fördert.
                </p>
              </TextBackground>
            </TextWidth>
          </FrontElement>,
          <FrontElement>
            <Scroller
              front={[
                <FrontElement distanceBottom={1000}>
                  <TextWidth>
                    <TextBackground>
                      <p>
                        Ein Laufstall, in dem sich die Rinder frei bewegen
                        können, muss generell so angelegt sein, dass die Rinder
                        einander ausweichen können. Der <b>Platz zum Fressen</b>{' '}
                        für jedes Tier muss 65 Zentimeter breit und 2,90 Meter
                        lang sein. Die Länge schliesst dabei einen 2,2 Meter
                        breiten Laufgang ein.
                      </p>
                      <p>
                        Eine an der Wand stehende <b>Liegebox</b> für ein Rind
                        muss nach den gesetzlichen Mindestanforderung{' '}
                        <b>1,1 Meter breit und 2,3 Meter lang</b> sein. Davon
                        müssen 1,65 Meter als Liegefläche eingestreut oder mit
                        einem weichen verformbaren Material ausgestattet sein.
                      </p>
                    </TextBackground>
                  </TextWidth>
                </FrontElement>,
              ]}
              back={(index) => {
                const AN_ELEM = (
                  <AnnotationElement
                    defaults={{
                      anchor: 'top',
                      distance: 50,
                      connect: false,
                    }}
                    annotations={getAnnotations_Kuh(
                      colorMode,
                      width,
                      'konventionell'
                    )}
                  >
                    <img
                      alt=''
                      src={
                        width > 831
                          ? require('../../assets/K_Large_' +
                              CM +
                              '_konventione.png')
                          : width > 542
                          ? require('../../assets/K_Medium_' +
                              CM +
                              '_konventione.png')
                          : require('../../assets/K_Small_' +
                              CM +
                              '_konventione.png')
                      }
                    />
                  </AnnotationElement>
                )
                return (
                  <BackElement>
                    <div style={{ position: 'relative' }}>
                      <TextWidth>
                        <StandardLabel>Gesetzliches Minimum</StandardLabel>
                      </TextWidth>
                      {width > 830 && <ContentWidth>{AN_ELEM}</ContentWidth>}
                      {width < 832 && <FullWidth>{AN_ELEM}</FullWidth>}
                    </div>
                  </BackElement>
                )
              }}
            />
          </FrontElement>,
          <FrontElement>
            <Scroller
              front={[
                <FrontElement distanceBottom={1000}>
                  <TextWidth>
                    <TextBackground>
                      <p>
                        Für die Rinderhaltung nach den Bio-Suisse-Richtlinien
                        sind die{' '}
                        <b>Anforderungen des Raus-Programms einzuhalten</b>, und
                        die Tiere müssen Zugang zu einer Weide haben. Der{' '}
                        <b>Laufhof</b> muss <b>pro Tier 10 Quadratmeter</b>{' '}
                        gross und permanent zugänglich sein. Für die Masse für
                        den Fressplatz und die Liegeboxen gilt die
                        Tierschutzverordnung.
                      </p>
                    </TextBackground>
                  </TextWidth>
                </FrontElement>,
              ]}
              back={(index) => {
                const AN_ELEM = (
                  <AnnotationElement
                    defaults={{
                      anchor: 'top',
                      distance: 120,
                      connect: false,
                    }}
                    annotations={getAnnotations_Kuh(colorMode, width, 'bio')}
                  >
                    <img
                      alt=''
                      src={
                        width > 831
                          ? require('../../assets/K_Large_' + CM + '_bio.png')
                          : width > 542
                          ? require('../../assets/K_Medium_' + CM + '_bio.png')
                          : require('../../assets/K_Small_' + CM + '_bio.png')
                      }
                    />
                  </AnnotationElement>
                )
                return (
                  <BackElement>
                    <div style={{ position: 'relative' }}>
                      <TextWidth>
                        <StandardLabel>Bio-Suisse</StandardLabel>
                      </TextWidth>
                      {width > 830 && <ContentWidth>{AN_ELEM}</ContentWidth>}
                      {width < 832 && <FullWidth>{AN_ELEM}</FullWidth>}
                    </div>
                  </BackElement>
                )
              }}
            />
          </FrontElement>,
          <FrontElement>
            <Scroller
              front={[
                <FrontElement distanceBottom={1000}>
                  <TextWidth>
                    <TextBackground>
                      <p>
                        In der EU ist die{' '}
                        <b>Haltung von Rindern kaum spezifisch geregelt</b>{' '}
                        &mdash; es gelten lediglich die allgemeinen Vorschriften
                        zur Tierhaltung. Auch in den einzelnen Mitgliedsstaaten
                        gibt es wenig Regeln, die darüber hinausgehen. In
                        Österreich gibt es ähnliche Vorgaben wie in der
                        Schweizer Tierschutzverordnung. Die Masse, die im
                        Laufstall eingehalten werden müssen, unterscheiden sich
                        jeweils nur um wenige Zentimeter.
                      </p>
                    </TextBackground>
                  </TextWidth>
                </FrontElement>,
              ]}
              back={(index) => {
                const AN_ELEM = (
                  <AnnotationElement
                    defaults={{
                      anchor: 'top',
                      distance: 120,
                      connect: false,
                    }}
                    annotations={getAnnotations_Kuh(colorMode, width, 'eu')}
                  >
                    <img
                      alt=''
                      src={
                        width > 831
                          ? require('../../assets/K_Large_' + CM + '_eu.png')
                          : width > 542
                          ? require('../../assets/K_Medium_' + CM + '_eu.png')
                          : require('../../assets/K_Small_' + CM + '_eu.png')
                      }
                    />
                  </AnnotationElement>
                )
                return (
                  <BackElement>
                    <div style={{ position: 'relative' }}>
                      <TextWidth>
                        <StandardLabel>EU-Richtlinien</StandardLabel>
                      </TextWidth>
                      {width > 830 && <ContentWidth>{AN_ELEM}</ContentWidth>}
                      {width < 832 && <FullWidth>{AN_ELEM}</FullWidth>}
                    </div>
                  </BackElement>
                )
              }}
            />
          </FrontElement>,
          <FrontElement distanceBottom={200}>
            <SizeCompare>
              <TextWidth>
                <TextBackground>
                  <h4>Vergleich der vorgeschriebenen minimalen Fläche</h4>
                  <AnnotationElement
                    defaults={{
                      distance: 0,
                    }}
                    annotations={[
                      {
                        left: 5,
                        top: 25,
                        anchor: 'top',
                        content: (
                          <Annotation
                            colorMode={colorMode}
                            style={{ minWidth: 100 }}
                          >
                            <span style={{ color: '#D3343F' }}>
                              Gesetzliches Minimum
                            </span>
                            <br />
                            4m<sup>2</sup>
                          </Annotation>
                        ),
                        // connect: true,
                        connector: {
                          type: 'straight',
                          style: { stroke: '#D3343F', strokeWidth: 1.5 },
                          offset: 0,
                        },
                      },
                      {
                        left: 70,
                        top: 25,
                        anchor: 'top',
                        content: (
                          <Annotation
                            align='left'
                            colorMode={colorMode}
                            style={{ minWidth: 80 }}
                          >
                            <span style={{ color: '#2CA29F' }}>Bio-Suisse</span>
                            <br />
                            14m<sup>2</sup>
                          </Annotation>
                        ),
                        // connect: true,
                        connector: {
                          type: 'straight',
                          style: { stroke: '#2CA29F', strokeWidth: 1.5 },
                          offset: 0,
                        },
                      },
                      {
                        left: 5,
                        top: 70,
                        anchor: 'bottom',
                        content: (
                          <Annotation
                            colorMode={colorMode}
                            style={{ minWidth: 80 }}
                          >
                            <span
                              style={{
                                color: CM === 'DM' ? '#e9e9e9' : '#202346',
                              }}
                            >
                              EU-Richtlinien
                            </span>
                            <br />
                            2,6m<sup>2</sup>
                          </Annotation>
                        ),
                        // connect: true,
                        connector: {
                          type: 'straight',
                          style: {
                            stroke: CM === 'DM' ? '#e9e9e9' : '#202346',
                            strokeWidth: 1.5,
                          },
                          offset: 0,
                        },
                      },
                    ]}
                  >
                    <img
                      alt=''
                      src={
                        CM === 'DM'
                          ? require('../../assets/K_DM.png')
                          : require('../../assets/K.png')
                      }
                    />
                  </AnnotationElement>
                </TextBackground>
              </TextWidth>
            </SizeCompare>
          </FrontElement>,
        ]}
        back={(index) => {
          console.log(index)

          let bgimage =
            width > 831
              ? require('../../assets/K_Large_' + CM + '_kuh_2.png')
              : width > 542
              ? require('../../assets/K_Medium_' + CM + '_kuh_2.png')
              : require('../../assets/K_Small_' + CM + '_kuh_2.png')
          if (index >= 1)
            bgimage =
              width > 831
                ? require('../../assets/K_Large_' + CM + '_kuh.png')
                : width > 542
                ? require('../../assets/K_Medium_' + CM + '_kuh.png')
                : require('../../assets/K_Small_' + CM + '_kuh.png')

          let El = <img alt='' src={bgimage} />
          let Label = 'Beispiel: Kuh mit 1,25 Metern Schulterhöhe'
          return (
            <BackElement>
              {index >= 1 && (
                <TextWidth>
                  <AnimalLabel>{Label}</AnimalLabel>
                </TextWidth>
              )}
              {width > 830 && <ContentWidth>{El}</ContentWidth>}
              {width < 832 && <FullWidth>{El}</FullWidth>}
            </BackElement>
          )
        }}
      />
    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)<{
  tenant?: string
  colorMode: 'light' | 'dark'
}>`
  max-width: 100%;
  background-color: var(--site-background);
  color: var(--text-color);
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: var(--font-sans-serif);
  color: var(--brandblue-main);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transform: translate3d(0, 0, 0);
  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }
  .App-logo {
    height: 40vmin;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: no-preference) {
    .App-logo {
      animation: App-logo-spin infinite 20s linear;
    }
  }

  .App-header {
    /* min-height: 100vh; */
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    .tenant {
      ${(props) => (props.tenant ? `color: var(--tenant-${props.tenant})` : '')}
    }
  }

  @keyframes App-logo-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

export default MainComponent

const FullWidth = styled.div`
  max-width: 100%;
  width: 100%;
  overflow-x: hidden;
`

const TextBackground = styled.div<{ special?: boolean }>`
  background-color: ${({ special }) =>
    special ? 'var(--text-color)' : 'var(--site-background)'};
  color: ${({ special }) =>
    !special ? 'var(--text-color)' : 'var(--site-background)'};

  /* max-width: ${({ special }) => (special ? undefined : '80%')}; */
  /* margin-left: ${({ special }) => (special ? undefined : '10%')}; */
  text-align: ${({ special }) => (special ? 'center' : 'left')};
  p,
  h4,
  h3 {
    color: ${({ special }) =>
      !special ? 'var(--text-color)' : 'var(--site-background)'};
  }
  h4 {
    margin-top: 0;
    margin-bottom: 10px;
  }
  border-radius: 5px;
  padding: 20px;
  -webkit-box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2),
    0px 0px 10px 0px rgba(255, 255, 255, 0.2);
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2),
    0px 0px 10px 0px rgba(255, 255, 255, 0.2);
`

const FrontElement = styled.div<{ [k: string]: any }>`
  font-size: 18px;
  line-height: 23px;
  font-family: var(--font-sans-serif);
  /* min-height: 100vh; */
  position: relative;
  z-index: 1001;
  max-width: 100%;
  p,
  h4,
  h3 {
    font-family: var(--font-sans-serif);
  }
  img {
    /* z-index: 0; */
    max-width: 100%;
  }
`
const BackElement = styled.div`
  /* padding-top: 4vh; */
  @media only screen and (max-width: 542px) {
    padding-top: 0;
  }
  img {
    max-width: 100%;
    @media only screen and (max-width: 542px) {
    }
  }
`
const StandardLabel = styled.div`
  font-weight: 700;
  /* font-style: italic; */
  line-height: 22px;
  color: var(--text-color);
  font-size: 30px;
  font-family: var(--font-sans-serif);
  width: 100%;
  text-align: center;
  margin-top: 64px;
  position: absolute;
  left: 0;
  @media only screen and (max-width: 542px) {
    font-size: 19px;
    margin-top: 40px;
  }
`
const AnimalLabel = styled.div`
  color: var(--text-color);

  font-size: 18px;
  font-family: var(--font-sans-serif);
  width: 100%;
  text-align: center;
  line-height: 22px;
  position: absolute;
  top: 30px;
  position: absolute;
  left: 0;
  font-weight: 300 !important;
  z-index: 10;

  @media only screen and (max-width: 542px) {
    font-size: 15px;
    top: 15px;
  }
`

const SizeCompare = styled.div`
  background-color: var(--site-background);
  position: relative;
  min-height: 400px;
  width: 100%;

  img {
    width: 100% !important;
  }
  h4 {
    padding-top: 1em;
    /* margin-bottom: 1em; */
    max-width: 70%;
    margin-left: 15%;
    font-weight: 400;
    text-align: center;
    font-size: 22px;
    line-height: 30px;
    @media only screen and (max-width: 542px) {
      font-size: 18px;
      line-height: 26px;
    }
  }
  /* div {
    position: absolute;
    top: 0;
    left: 0;
    display: inline-block;
    &.ch {
      border: 1px solid #f00;
      width: 50%;
      padding-bottom: 50%;
    }
  } */
`
